<template>
    <div>
        <div class="d-flex justify-content-center">
            <form role="form" class="mt-4 col-12 col-md-6" @submit.prevent="validateCertificate">
                <h1 class="text-center">{{$t('certificate.validate_description')}}</h1>
                <base-input
                    v-model="code"
                    :name="$t('certificate.code')"
                    class="mb-3 "
                    :placeholder="$t('certificate.code')"
                    prepend-icon="mdi mdi-certificate"
                    v-validate="'required'"
                ></base-input>
                <div class="text-center">
                    <base-button
                        @click.stop="validateCertificate"
                        :loading="loading"
                        :style="[$bgColor(color.button || color.primary), { color: color.secondary }]"
                        class="my-4"
                    >
                        {{ $t('certificate.validate_button') }}
                    </base-button>
                </div>
            </form>
        </div>

        <!-- Estado da validação (backlog#344) -->
        <div v-if="state" class="d-flex justify-content-center">
            <div class="col-12 col-md-6 nch--validation-state" :class="`nch--validation-state__${state}`">
                <div class="d-flex align-items-start">
                    <i class="fa-2x mr-3" :class="stateIcon"></i>
                    <div class="flex-grow-1">
                        <div class="h4 mb-1">{{ $t(`certificate.state.${state}`) }}</div>
                        <div class="small">{{ $t(`certificate.state.${state}_text`) }}</div>
                        <dl v-if="summary" class="row small mb-0 mt-3">
                            <dt class="col-5 col-sm-4">{{ $t('certificate.summary.student') }}</dt><dd class="col-7 col-sm-8">{{ summary.student || '—' }}</dd>
                            <dt class="col-5 col-sm-4">{{ $t('certificate.summary.content') }}</dt><dd class="col-7 col-sm-8">{{ summary.content || '—' }}</dd>
                            <dt class="col-5 col-sm-4">{{ $t('certificate.summary.emitted_at') }}</dt><dd class="col-7 col-sm-8">{{ date(summary.emitted_at) }}</dd>
                            <dt v-if="summary.valid_until" class="col-5 col-sm-4">{{ $t('certificate.summary.valid_until') }}</dt><dd v-if="summary.valid_until" class="col-7 col-sm-8">{{ date(summary.valid_until) }}</dd>
                            <dt class="col-5 col-sm-4">{{ $t('certificate.summary.code') }}</dt><dd class="col-7 col-sm-8"><code>{{ summary.code }}</code></dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-center" v-if="certificate">
            <div>
                <div class="col-12 text-center">
                    <certificate-toolbar :certificate="certificate" :print="false"></certificate-toolbar>
                </div>
                <div id="certificate__wrapper">
                    <certificate-tag-draggable v-if="certificate.front.length > 0" :certificate="certificate" side="front"></certificate-tag-draggable>
                    <certificate-tag-draggable v-if="certificate.back.length > 0" :certificate="certificate" side="back"></certificate-tag-draggable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import BaseButton from '../../argon/components/BaseButton';
    import BaseInput from '../../argon/components/Inputs/BaseInput';
    import CertificateTagDraggable from "./CertificateTagDraggable";
    import CertificateToolbar from "./CertificateToolbar";

    /**
     * Página pública de validação (backlog#344): aceita ?codigo= (ou ?code=) e
     * valida sozinha; mostra o estado (válido, expirado, não encontrado,
     * validação desligada) e, quando houver, o certificado como antes.
     */
    export default {
        name: "CertificateValidate",

        components: {
            BaseButton,
            BaseInput,
            CertificateTagDraggable,
            CertificateToolbar
        },

        props: {
            color: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                form: [],
                loading: false,
                code: '',
                certificate: false,
                state: null,
                summary: null,
            }
        },

        computed: {
            stateIcon() {
                return { valid: 'fas fa-check-circle text-success', expired: 'fas fa-clock text-warning', not_found: 'fas fa-times-circle text-danger', disabled: 'fas fa-lock text-muted' }[this.state] || '';
            },
        },

        created() {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('codigo') || params.get('code');
            if (code) {
                this.code = code.trim();
                this.$nextTick(() => this.validateCertificate(true));
            }
        },

        methods: {
            date(value) {
                if (!value) return '—';
                const d = new Date(value);
                return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(localStorage.getItem('lang') || 'pt-BR');
            },

            applyResult(res) {
                const data = res && res.data ? res.data : {};
                this.state = data.state || 'valid';
                this.summary = data.summary || null;
                if (data.certificate) {
                    this.certificate = data.certificate;
                    this.$notify({ type: this.state === 'expired' ? 'warning' : 'success', message: data.message || this.$t('certificate.success') });
                } else {
                    this.$notify({ type: 'warning', message: data.message || this.$t(`certificate.state.${this.state}`) });
                }
            },

            applyError(err) {
                const data = (err && err.response && err.response.data) || {};
                this.state = data.state || 'not_found';
                this.summary = null;
                this.$notify({ type: 'danger', message: data.error || this.$t('certificate.error') });
            },

            validateCertificate(auto = false) {
                const run = () => {
                    this.certificate = false;
                    this.state = null;
                    this.summary = null;
                    this.loading = true;

                    this.$store.dispatch('CHECK_CERTIFICATE', { code: this.code })
                        .then((res) => {
                            if (res && res.status === 200) {
                                this.applyResult(res);
                            } else {
                                this.applyError(res);
                            }
                            this.loading = false;
                        })
                        .catch((error) => {
                            this.applyError(error);
                            this.loading = false;
                        });
                };

                if (auto === true) {
                    if (this.code) run();
                    return;
                }

                this.$validator.validate().then((valid) => {
                    if (valid) {
                        run();
                    } else {
                        this.$notify({ type: 'danger', message: this.$t('certificate.error') });
                        this.loading = false;
                    }
                });
            }
        }
    }
</script>

<style scoped>
.nch--validation-state { border-radius: 10px; padding: 16px 20px; margin-bottom: 16px; border: 1px solid #e3e6ea; background: #fff; }
.nch--validation-state__valid { border-color: #2dce89; background: #f3fcf7; }
.nch--validation-state__expired { border-color: #fb6340; background: #fff7f3; }
.nch--validation-state__not_found { border-color: #f5365c; background: #fff4f6; }
.nch--validation-state__disabled { border-color: #adb5bd; background: #f8f9fa; }
dl dt { font-weight: 600; }
</style>
